import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TableSortLabel,
  CircularProgress,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Users, Calendar, TrendingUp, Download, Eye } from 'lucide-react';
import { adminAPI } from '../services/api';
import { PageHeader, EmptyState } from '../components/ui';
import EventInquiriesModal from '../components/EventInquiriesModal';
import EditRegistrationModal from '../components/EditRegistrationModal.jsx';
import EditTicketGroupModal from '../components/EditTicketGroupModal.jsx';
import RegistrationStatusHover from '../components/RegistrationStatusHover.jsx';
import { registrationStatusLabel } from '../constants/registrationStatuses.js';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { formatAdminInr } from '../utils/formatAdminPrice.js';
import { packageDetailSummary } from '../utils/packageDisplayText.js';
import { isNonPayingRegistration } from '../utils/registrationStaffStatus.js';
import {
  getBookingParticipantCount,
  getEffectiveRegistrationStatus,
  getParticipantPaymentState,
  isParticipantFullyPaid,
  bookingFeeTotal,
  suggestParticipantReceiptAmount,
  resolveBookingPackage
} from '../utils/participantPaymentShare.js';
import {
  ADVANCE_WINDOW_DAYS,
  REMAINING_DUE_DAYS_BEFORE_DEPARTURE,
  effectiveBookingAdvance,
  hasPartialAdvanceStructure
} from '../utils/bookingAdvanceAmounts.js';
import { formatPersonDisplayName } from '../utils/capitalizePersonName.js';
import { RECEIPT_PAYMENT_METHOD_OPTIONS } from '../constants/receiptPaymentMethods.js';
import { resolveTicketPnrs } from '../utils/ticketPnr.js';
import { bookingHasTicketFiles, bookingInvolvesTickets, resolveTicketPackageSnapshot } from '../utils/ticketPackage.js';
import { formatBookingStayDisplay } from '../utils/formatStayOptionLabel.js';
import './Registrations.css';

// Utility for formatting dates
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

function formatPhoneForTableDisplay(raw) {
  if (raw == null || String(raw).trim() === '') return '—';
  let d = String(raw).replace(/\D/g, '');
  if (!d) return '—';
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d || '—';
}

function formatGenderDisplay(raw) {
  if (raw == null || String(raw).trim() === '') return '—';
  const s = String(raw).trim().toLowerCase();
  if (s === 'male') return 'Male';
  if (s === 'female') return 'Female';
  if (s === 'other') return 'Other';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatAgeFromBirthdate(birthdate) {
  if (birthdate == null || String(birthdate).trim() === '') return '—';
  const raw = String(birthdate).trim();
  const d = raw.length >= 10 ? new Date(`${raw.slice(0, 10)}T12:00:00`) : new Date(raw);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  if (age < 0 || age > 130) return '—';
  return String(age);
}

function primaryTravelerDob(primary, reg) {
  return (
    primary?.dateOfBirth ||
    primary?.birthdate ||
    primary?.dob ||
    reg?.dob ||
    reg?.dateOfBirth ||
    null
  );
}

function bookingPrimaryDisplayName(booking) {
  const reg = booking?.registration || null;
  const primary = Array.isArray(booking?.travelers) && booking.travelers[0] ? booking.travelers[0] : null;
  return formatPersonDisplayName(
    primary?.firstName || reg?.firstName,
    primary?.lastName || reg?.lastName
  );
}

function bookingBatchDateLabel(booking) {
  const start = booking?.batch?.startDate;
  const end = booking?.batch?.endDate;
  if (!start && !end) return '—';
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
  return formatDate(start || end);
}

// Component for Top KPI Cards
const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color }}>
          {value || 0}
        </Typography>
      </Box>
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}15`, color }}>
        {icon}
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { data, isLoading } = useQuery(['admin-dashboard-stats'], () => adminAPI.getDashboardStats({}));

  const stats = data?.data?.data || {};
  const topCards = stats.topCards || {};
  const upcomingCampStrength = stats.upcomingCampStrength || [];
  const trekCatalog = stats.trekCatalog || [];
  const leadsVsRegistrations = stats.leadsVsRegistrations || [];

  // Matrix Processing for Upcoming Camp Strength
  const matrixData = useMemo(() => {
    if (!upcomingCampStrength.length) return { dates: [], trekIds: [], matrix: {} };

    const dateSet = new Set();
    const trekIdSet = new Set();
    const matrix = {};

    upcomingCampStrength.forEach((item) => {
      dateSet.add(item.startDate);
      trekIdSet.add(item.trekId);

      if (!matrix[item.startDate]) {
        matrix[item.startDate] = {};
      }
      matrix[item.startDate][item.trekId] = item.confirmedCount;
    });

    const dates = Array.from(dateSet).sort();
    const trekIds = Array.from(trekIdSet).sort((a, b) => Number(a) - Number(b));

    return { dates, trekIds, matrix };
  }, [upcomingCampStrength]);

  // Sort State for Leads vs Registrations

  const [pageCamp, setPageCamp] = useState(0);
  const [rowsCamp, setRowsCamp] = useState(10);
  const [pageTrek, setPageTrek] = useState(0);
  const [rowsTrek, setRowsTrek] = useState(10);
  const [pageRecentReg, setPageRecentReg] = useState(0);
  const [rowsRecentReg, setRowsRecentReg] = useState(10);
  const [pageLeads, setPageLeads] = useState(0);
  const [rowsLeads, setRowsLeads] = useState(10);

  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { permissions, can } = useAuth();
  const canManageRegistrations =
    permissions.includes('*') || permissions.includes('registrations_manage');
  const canExportRegistrationsCsv = can('registrations_export') || can('registrations_manage');
  const canViewTickets = can('tickets') || can('tickets_manage') || canManageRegistrations;
  const showRegistrationActions = canManageRegistrations || canViewTickets;

  // Fetch Recent Registrations (with pagination)
  const { data: recentRegData, isLoading: recentRegistrationsLoading } = useQuery(
    ['admin-dashboard-recent-registrations', pageRecentReg, rowsRecentReg],
    () =>
      adminAPI.getBookings({
        page: pageRecentReg + 1,
        limit: rowsRecentReg,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }),
    { keepPreviousData: true, staleTime: 30_000 }
  );

  const recentRegistrations = recentRegData?.data?.data || [];
  const recentTotalCount = recentRegData?.data?.pagination?.total ?? recentRegData?.data?.meta?.total ?? recentRegistrations.length;

  const displayRows = useMemo(
    () =>
      recentRegistrations.flatMap((booking) => {
        const rawTravelers = Array.isArray(booking?.travelers) ? booking.travelers : [];
        const travelers = rawTravelers.filter((traveler) => {
          if (!traveler || typeof traveler !== 'object') return false;
          const hasName =
            String(traveler.firstName || '').trim() || String(traveler.lastName || '').trim();
          const hasPhone = String(traveler.phone || '').trim();
          return Boolean(hasName || hasPhone);
        });
        const countCap = Math.max(1, Number(booking?.travelerCount) || 1);
        const capped =
          travelers.length > countCap ? travelers.slice(0, countCap) : travelers;
        if (!capped.length) {
          return [{ booking, traveler: null, travelerIndex: 0, rowKey: `${booking?.id ?? 'booking'}-fallback` }];
        }
        return capped.map((traveler, travelerIndex) => ({
          booking,
          traveler,
          travelerIndex,
          rowKey: `${booking?.id ?? 'booking'}-traveler-${travelerIndex}`
        }));
      }),
    [recentRegistrations]
  );

  const [receiptModal, setReceiptModal] = useState({
    open: false,
    booking: null,
    bookingLoading: false,
    participantName: null,
    participantIndex: null,
    participantCount: 1
  });
  const [editModal, setEditModal] = useState({ open: false, bookingId: null });
  const [ticketModal, setTicketModal] = useState({ open: false, row: null });
  const [exporting, setExporting] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    amount: '',
    notes: '',
    method: 'cash'
  });

  const addReceiptMutation = useMutation(
    ({ bookingId, body }) => adminAPI.addBookingReceipt(bookingId, body),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('admin-dashboard-recent-registrations');
        queryClient.invalidateQueries('admin-dashboard-stats');
        setReceiptModal({
          open: false,
          booking: null,
          bookingLoading: false,
          participantName: null,
          participantIndex: null,
          participantCount: 1
        });
        setReceiptForm({ amount: '', notes: '', method: 'cash' });
        const bookingData = res?.data?.data;
        const rn = Array.isArray(bookingData?.receipts) ? bookingData.receipts[0]?.receiptNumber : null;
        showToast({
          message: rn ? `Receipt saved (${rn}).` : 'Receipt saved.',
          severity: 'success'
        });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to add receipt',
          severity: 'error'
        })
    }
  );

  const openEditRegistration = (row) => {
    if (!row?.id) {
      showToast({ message: 'Missing registration id for this row.', severity: 'warning' });
      return;
    }
    setEditModal({ open: true, bookingId: row.id });
  };

  const openTicketModal = (row) => {
    if (!row?.id) {
      showToast({ message: 'Missing booking for this row.', severity: 'warning' });
      return;
    }
    setTicketModal({
      open: true,
      row: { ...row, ticketPackage: resolveTicketPackageSnapshot(row) }
    });
  };

  const openReceiptModal = async ({ booking, traveler, travelerIndex }) => {
    const idx = Number.isFinite(travelerIndex) ? travelerIndex : 0;
    const initialPayment = getParticipantPaymentState(booking, traveler, idx);
    setReceiptModal({
      open: true,
      booking,
      bookingLoading: true,
      participantName: initialPayment.participantName || null,
      participantIndex: idx,
      participantCount: initialPayment.count
    });
    setReceiptForm({
      amount: suggestParticipantReceiptAmount(initialPayment, booking),
      notes: '',
      method: 'cash'
    });

    try {
      const res = await adminAPI.getBooking(booking.id);
      const fresh = res?.data?.data || booking;
      const payment = getParticipantPaymentState(fresh, traveler, idx);
      setReceiptModal((prev) => ({
        ...prev,
        booking: fresh,
        bookingLoading: false,
        participantName: payment.participantName || prev.participantName,
        participantCount: payment.count
      }));
      setReceiptForm((prev) => ({
        ...prev,
        amount: suggestParticipantReceiptAmount(payment, fresh) || prev.amount
      }));
    } catch {
      setReceiptModal((prev) => ({ ...prev, bookingLoading: false }));
      showToast({
        message: 'Could not refresh payment details. Amounts may be incomplete until the API is available.',
        severity: 'warning'
      });
    }
  };

  const submitReceipt = () => {
    const b = receiptModal.booking;
    if (!b?.id) return;
    const amt = Number(receiptForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      showToast({ message: 'Enter a valid amount.', severity: 'warning' });
      return;
    }
    const noteRaw = String(receiptForm.notes || '').trim();
    const participantLabel = receiptModal.participantName
      ? `Participant: ${receiptModal.participantName}${
          receiptModal.participantCount > 1 && Number.isFinite(receiptModal.participantIndex)
            ? ` (${Number(receiptModal.participantIndex) + 1}/${receiptModal.participantCount})`
            : ''
        }`
      : '';
    const participantPrefix = participantLabel;
    const noteWithParticipant = participantPrefix
      ? (noteRaw ? `${participantPrefix} | ${noteRaw}` : participantPrefix)
      : (noteRaw || undefined);
    addReceiptMutation.mutate({
      bookingId: b.id,
      body: {
        amount: amt,
        paidAt: new Date().toISOString(),
        notes: noteWithParticipant,
        method: receiptForm.method || 'office'
      }
    });
  };

  const handleExportRecentRegistrations = async () => {
    setExporting(true);
    try {
      const res = await adminAPI.exportRegistrationsCsv({
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      const truncated = String(res.headers['x-export-truncated'] || '').toLowerCase() === 'true';
      let filename = `registrations-export-${new Date().toISOString().slice(0, 10)}.csv`;
      const cd = res.headers['content-disposition'] || '';
      const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
      if (star) {
        try {
          filename = decodeURIComponent(star[1].trim()) || filename;
        } catch {}
      } else {
        const q = cd.match(/filename="([^"]+)"/i);
        if (q?.[1]) filename = q[1];
      }
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      showToast({
        message: truncated
          ? 'Export downloaded (row limit reached).'
          : 'Export downloaded.',
        severity: truncated ? 'warning' : 'success'
      });
    } catch (err) {
      const msg = err.response?.data?.message;
      showToast({ message: msg ? String(msg) : 'Export failed.', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState({ trekId: null, eventName: '' });
  const [searchLeads, setSearchLeads] = useState('');
  const [conversionFilter, setConversionFilter] = useState('');
  const [sortField, setSortField] = useState('inquiries');
  const [sortOrderDir, setSortOrderDir] = useState('desc');


  const mappedLeads = useMemo(() => {
    return leadsVsRegistrations.map(row => {
      const convRate = row.inquiries > 0 ? ((row.confirmed / row.inquiries) * 100).toFixed(2) : 0;
      return { ...row, conversionRate: parseFloat(convRate) };
    });
  }, [leadsVsRegistrations]);

  const processedLeads = useMemo(() => {
    let result = [...mappedLeads];

    if (searchLeads) {
      result = result.filter(r => (r.month || '').toLowerCase().includes(searchLeads.toLowerCase()));
    }

    if (conversionFilter) {
      result = result.filter(r => {
        if (conversionFilter === 'high') return r.conversionRate >= 50;
        if (conversionFilter === 'medium') return r.conversionRate >= 20 && r.conversionRate < 50;
        if (conversionFilter === 'low') return r.conversionRate < 20;
        return true;
      });
    }

    result.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];
      if (sortField === 'month') { valA = valA || ''; valB = valB || ''; }
      if (valA < valB) return sortOrderDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrderDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [mappedLeads, searchLeads, conversionFilter, sortField, sortOrderDir]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader title="Admin Dashboard" />

      {/* 1. Top Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
        <StatCard
          title="Today Total Confirmed"
          value={topCards.todayConfirmed}
          icon={<TrendingUp size={24} />}
          color="#10b981"
        />
        <StatCard
          title="Today Registration"
          value={topCards.todayRegistration}
          icon={<Users size={24} />}
          color="#3b82f6"
        />
        <StatCard
          title="This Month Confirmed"
          value={topCards.thisMonthConfirmed}
          icon={<Calendar size={24} />}
          color="#8b5cf6"
        />
        <StatCard
          title="Yesterday Total Confirmed"
          value={topCards.yesterdayConfirmed}
          icon={<TrendingUp size={24} />}
          color="#10b981"
        />
        <StatCard
          title="Yesterday Registration"
          value={topCards.yesterdayRegistration}
          icon={<Users size={24} />}
          color="#3b82f6"
        />
        <StatCard
          title="Last Month Confirmed"
          value={topCards.lastMonthConfirmed}
          icon={<Calendar size={24} />}
          color="#8b5cf6"
        />
      </Box>

      {/* 2 & 3. Upcoming Camp Strength and Trek Catalog Map */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, mb: 4 }}>

        {/* 3/4 Upcoming Camp Strength Matrix */}
        <Box sx={{ flex: 3, minWidth: 0 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Upcoming Camp Strength</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--border-color)' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'var(--bg-color)' }}>
                  <TableCell sx={{ fontWeight: 600, minWidth: 150, position: 'sticky', left: 0, bgcolor: 'var(--bg-color)', zIndex: 2 }}>Events → Start Date: Day</TableCell>
                  {matrixData.trekIds.map((tid) => (
                    <TableCell key={tid} align="center" sx={{ fontWeight: 600 }}>
                      <Chip label={tid} size="small" variant="outlined" sx={{ color: 'var(--primary-color)', borderColor: 'var(--primary-color)' }} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixData.dates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={matrixData.trekIds.length + 1} align="center" sx={{ py: 3 }}>
                      No upcoming camps found.
                    </TableCell>
                  </TableRow>
                ) : (
                  matrixData.dates.slice(pageCamp * rowsCamp, pageCamp * rowsCamp + rowsCamp).map((dateStr) => (
                    <TableRow key={dateStr} hover>
                      <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>{formatDate(dateStr)}</TableCell>
                      {matrixData.trekIds.map((tid) => {
                        const count = matrixData.matrix[dateStr]?.[tid];
                        return (
                          <TableCell key={tid} align="center">
                            {count ? (
                              <Chip
                                label={count}
                                size="small"
                                sx={{
                                  bgcolor: 'var(--primary-color)',
                                  color: 'black',
                                  fontWeight: 'semibold',
                                  height: 24
                                }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={matrixData.dates.length}
            page={pageCamp}
            onPageChange={(e, newPage) => setPageCamp(newPage)}
            rowsPerPage={rowsCamp}
            onRowsPerPageChange={(e) => { setRowsCamp(parseInt(e.target.value, 10)); setPageCamp(0); }}
            rowsPerPageOptions={[]}
          />
        </Box>

        {/* 1/4 Trek Catalog Map */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Trek Name Catalog Map</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--border-color)', maxHeight: 600, overflowY: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'var(--bg-color)' }}>Trek ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'var(--bg-color)' }}>Trek Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trekCatalog.slice(pageTrek * rowsTrek, pageTrek * rowsTrek + rowsTrek).map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Chip label={t.id} size="small" sx={{ bgcolor: '#0f172a', color: 'white' }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{t.name}</TableCell>
                  </TableRow>
                ))}
                {trekCatalog.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">No treks found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={trekCatalog.length}
            page={pageTrek}
            onPageChange={(e, newPage) => setPageTrek(newPage)}
            rowsPerPage={rowsTrek}
            onRowsPerPageChange={(e) => { setRowsTrek(parseInt(e.target.value, 10)); setPageTrek(0); }}
            rowsPerPageOptions={[]}
          />
        </Box>
      </Box>

      {/* 4. Last 10 Registrations (Matching Registrations Table Design) */}
      <Box sx={{ mb: 4 }}>
        <Card className="ops-card reg-table-card" sx={{ mb: 0, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Registrations ({recentTotalCount})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {canExportRegistrationsCsv ? (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={recentRegistrationsLoading || exporting}
                  onClick={handleExportRecentRegistrations}
                  startIcon={<Download size={16} />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {exporting ? 'Preparing…' : 'Download Excel (CSV)'}
                </Button>
              ) : null}
              <Button
                component={Link}
                to="/registrations"
                size="small"
                variant="outlined"
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                View All Registrations →
              </Button>
            </Box>
          </Box>

          {recentRegistrationsLoading ? (
            <div className="ops-loading-box" style={{ padding: '3rem 1.5rem', textAlign: 'center' }}>
              <CircularProgress size={32} sx={{ color: 'primary.main', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Loading registrations…</Typography>
            </div>
          ) : displayRows.length === 0 ? (
            <EmptyState
              title="No registrations found"
              description="No recent registrations available."
            />
          ) : (
            <div className="reg-table-wrap">
              <table className="table" style={{ minWidth: 1200, whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th>REG ID</th>
                    <th>PACKAGE</th>
                    <th>GROUP</th>
                    <th>NAME</th>
                    <th>AGE</th>
                    <th>GENDER</th>
                    <th>PHONE</th>
                    <th>STAY</th>
                    <th>STATUS</th>
                    {showRegistrationActions && <th style={{ textAlign: 'right' }}>ACTIONS</th>}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map(({ booking, traveler, travelerIndex, rowKey }) => {
                    const row = booking;
                    const pkg = resolveBookingPackage(row);
                    const reg = row.registration || null;
                    const displayTraveler = traveler || (Array.isArray(row.travelers) && row.travelers[0] ? row.travelers[0] : null);
                    const displayName = formatPersonDisplayName(
                      displayTraveler?.firstName || reg?.firstName,
                      displayTraveler?.lastName || reg?.lastName
                    );
                    const initials = displayName.split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2);
                    const groupFromRegistration = row.registration?.group || null;
                    const groupNameOnly = String(groupFromRegistration?.groupName || '').trim() || String(row.groupName || '').trim() || initials || null;
                    const groupNumber = Number(groupFromRegistration?.id || row.groupNumber || 0) > 0 ? Number(groupFromRegistration?.id || row.groupNumber) : null;
                    const groupName = groupNameOnly ? `${groupNameOnly}${groupNumber ? ` (#${groupNumber})` : ''}` : '—';
                    const primaryGender = formatGenderDisplay(displayTraveler?.gender || reg?.gender);
                    const primaryAge = formatAgeFromBirthdate(primaryTravelerDob(displayTraveler, reg));
                    const primaryPhone = formatPhoneForTableDisplay(displayTraveler?.phone || reg?.phone);
                    const stayOpt = formatBookingStayDisplay(row);
                    const regStatus = getEffectiveRegistrationStatus(
                      row,
                      displayTraveler,
                      travelerIndex,
                      reg?.status
                    );
                    const statusLabel = registrationStatusLabel(regStatus);
                    const pkgDescFull = packageDetailSummary(pkg);
                    const pkgDescPreview = pkgDescFull.length > 160 ? `${pkgDescFull.slice(0, 160).trim()}…` : pkgDescFull;
                    const participantCount = getBookingParticipantCount(row);
                    const paymentState = getParticipantPaymentState(row, displayTraveler, travelerIndex);
                    const registrationIdDisplay =
                      row.registrationId != null
                        ? String(row.registrationId)
                        : reg?.id != null
                          ? String(reg.id)
                          : '—';

                    return (
                      <tr key={rowKey}>
                        <td>
                          <Tooltip
                            title={
                              traveler
                                ? `Participant ${travelerIndex + 1} of ${participantCount}`
                                : 'Registration id'
                            }
                            enterDelay={400}
                          >
                            <span className="reg-cell-id">{registrationIdDisplay}</span>
                          </Tooltip>
                        </td>
                        <td style={{ maxWidth: 280, verticalAlign: 'top', whiteSpace: 'normal' }}>
                          <div className="reg-cell-pkg-name">{pkg?.name || '—'}</div>
                          {pkgDescPreview ? (
                            <Tooltip title={pkgDescFull} placement="top" enterDelay={400}>
                              <div className="reg-cell-pkg-desc">{pkgDescPreview}</div>
                            </Tooltip>
                          ) : null}
                        </td>
                        <td className="reg-cell-group">{groupName}</td>
                        <td className="reg-cell-name">{displayName}</td>
                        <td className="reg-cell-muted">{primaryAge}</td>
                        <td className="reg-cell-muted">{primaryGender}</td>
                        <td className="reg-cell-muted">{primaryPhone}</td>
                        <td className="reg-cell-muted">{stayOpt}</td>
                        <td>
                          <RegistrationStatusHover
                            booking={row}
                            traveler={displayTraveler}
                            travelerIndex={travelerIndex}
                            status={regStatus}
                            statusLabel={statusLabel}
                          />
                        </td>
                        {showRegistrationActions && (
                          <td style={{ textAlign: 'right' }}>
                            <div className="reg-actions">
                              {canManageRegistrations ? (
                                <Tooltip title="Edit name, event, package, or status">
                                  <button type="button" className="reg-btn-edit" onClick={() => openEditRegistration(row)}>Edit</button>
                                </Tooltip>
                              ) : null}
                              {canViewTickets &&
                              travelerIndex === 0 &&
                              (bookingInvolvesTickets(row) || bookingHasTicketFiles(row)) ? (
                                <Tooltip title="View uploaded tickets">
                                  <button
                                    type="button"
                                    className="reg-btn-view"
                                    aria-label="View tickets"
                                    onClick={() => openTicketModal(row)}
                                  >
                                    <Eye size={16} />
                                  </button>
                                </Tooltip>
                              ) : null}
                              {canManageRegistrations && !isNonPayingRegistration(row) ? (
                                <Tooltip
                                  title={
                                    isParticipantFullyPaid(row, displayTraveler, travelerIndex)
                                      ? 'This participant is fully paid'
                                      : `Record payment for ${paymentState.participantName || 'participant'} (₹${Math.round(paymentState.pending)} pending)`
                                  }
                                >
                                  <span>
                                    <button
                                      type="button"
                                      className="reg-btn-pay"
                                      disabled={isParticipantFullyPaid(row, displayTraveler, travelerIndex)}
                                      onClick={() =>
                                        openReceiptModal({
                                          booking: row,
                                          traveler: displayTraveler,
                                          travelerIndex
                                        })
                                      }
                                    >
                                      Pay
                                    </button>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <TablePagination
            component="div"
            count={recentTotalCount}
            page={pageRecentReg}
            onPageChange={(e, newPage) => setPageRecentReg(newPage)}
            rowsPerPage={rowsRecentReg}
            onRowsPerPageChange={(e) => {
              setRowsRecentReg(parseInt(e.target.value, 10));
              setPageRecentReg(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </Card>
      </Box>

      {/* 5. Lead Inquiries vs Confirmed Registrations */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Customer Inquiries vs Confirmed Registrations (Last 4 Months)</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Search Event"
              size="small"
              variant="outlined"
              value={searchLeads}
              onChange={(e) => setSearchLeads(e.target.value)}
            />
            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel>Conversion</InputLabel>
              <Select value={conversionFilter} label="Conversion" onChange={(e) => setConversionFilter(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="high">High (&gt;50%)</MenuItem>
                <MenuItem value="medium">Medium (20-50%)</MenuItem>
                <MenuItem value="low">Low (&lt;20%)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--border-color)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'var(--bg-color)' }}>
                <TableCell>
                  <TableSortLabel active={sortField === 'month'} direction={sortField === 'month' ? sortOrderDir : 'asc'} onClick={() => {
                    const isAsc = sortField === 'month' && sortOrderDir === 'asc';
                    setSortOrderDir(isAsc ? 'desc' : 'asc');
                    setSortField('month');
                  }}>
                    <b>Event Name</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortField === 'inquiries'} direction={sortField === 'inquiries' ? sortOrderDir : 'asc'} onClick={() => {
                    const isAsc = sortField === 'inquiries' && sortOrderDir === 'asc';
                    setSortOrderDir(isAsc ? 'desc' : 'asc');
                    setSortField('inquiries');
                  }}>
                    <b>Total Inquiries</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortField === 'confirmed'} direction={sortField === 'confirmed' ? sortOrderDir : 'asc'} onClick={() => {
                    const isAsc = sortField === 'confirmed' && sortOrderDir === 'asc';
                    setSortOrderDir(isAsc ? 'desc' : 'asc');
                    setSortField('confirmed');
                  }}>
                    <b>Confirmed Registrations</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortField === 'conversionRate'} direction={sortField === 'conversionRate' ? sortOrderDir : 'asc'} onClick={() => {
                    const isAsc = sortField === 'conversionRate' && sortOrderDir === 'asc';
                    setSortOrderDir(isAsc ? 'desc' : 'asc');
                    setSortField('conversionRate');
                  }}>
                    <b>Conversion Rate</b>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedLeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    No data available.
                  </TableCell>
                </TableRow>
              ) : (
                processedLeads.slice(pageLeads * rowsLeads, pageLeads * rowsLeads + rowsLeads).map((row, i) => (
                  <TableRow
                    key={i}
                    hover
                    onClick={() => { setSelectedEvent({ trekId: row.trekId, eventName: row.month }); setModalOpen(true); }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{row.month}</TableCell>
                    <TableCell align="right">{row.inquiries}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={row.confirmed}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="right">{row.conversionRate}%</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={processedLeads.length}
          page={pageLeads}
          onPageChange={(e, newPage) => setPageLeads(newPage)}
          rowsPerPage={rowsLeads}
          onRowsPerPageChange={(e) => { setRowsLeads(parseInt(e.target.value, 10)); setPageLeads(0); }}
          rowsPerPageOptions={[]}
        />
      </Box>

      <EventInquiriesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        trekId={selectedEvent.trekId}
        eventName={selectedEvent.eventName}
      />

      <EditRegistrationModal
        open={editModal.open}
        bookingId={editModal.bookingId}
        onClose={() => setEditModal({ open: false, bookingId: null })}
        onSaved={() => {
          queryClient.invalidateQueries('admin-dashboard-recent-registrations');
          queryClient.invalidateQueries('admin-dashboard-stats');
        }}
      />

      <EditTicketGroupModal
        open={ticketModal.open}
        row={ticketModal.row}
        canManage={false}
        onClose={() => setTicketModal({ open: false, row: null })}
        onSaved={() => {
          queryClient.invalidateQueries('admin-dashboard-recent-registrations');
          queryClient.invalidateQueries('admin-dashboard-stats');
        }}
      />

      <Dialog
        open={receiptModal.open}
        onClose={() =>
          setReceiptModal({
            open: false,
            booking: null,
            bookingLoading: false,
            participantName: null,
            participantIndex: null,
            participantCount: 1
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add manual receipt</DialogTitle>
        <DialogContent>
          {receiptModal.bookingLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Loading payment details…
            </Typography>
          ) : null}
          {receiptModal.booking && !receiptModal.bookingLoading && (() => {
            const b = receiptModal.booking;
            const total = bookingFeeTotal(b);
            const advanceInfo = effectiveBookingAdvance({ ...b, totalAmount: total });
            const advance = advanceInfo.advanceAmount;
            const participantName = receiptModal.participantName || bookingPrimaryDisplayName(b);
            const participantCount = Number(receiptModal.participantCount) > 0 ? Number(receiptModal.participantCount) : 1;
            const participantLabelSuffix =
              participantCount > 1 && Number.isFinite(receiptModal.participantIndex)
                ? ` (${Number(receiptModal.participantIndex) + 1}/${participantCount})`
                : '';
            const participantPayment = getParticipantPaymentState(
              b,
              null,
              receiptModal.participantIndex ?? 0
            );
            const participantTotal = participantPayment.share;
            const participantAdvance = participantPayment.participantAdvance;
            const participantPaid = participantPayment.paid;
            const participantPending = participantPayment.pending;
            const participantAdvanceDue = participantPayment.advanceDue;
            const showParticipantAdvanceFill =
              hasPartialAdvanceStructure(participantTotal, participantAdvance) && participantAdvanceDue > 0;
            const showPaymentRuleHint =
              (advanceInfo.isWithinAdvanceWindow && total > 0) ||
              (!advanceInfo.isWithinAdvanceWindow && hasPartialAdvanceStructure(total, advance));
            const trekName = b.batch?.trek?.name || '—';
            const batchDates = bookingBatchDateLabel(b);
            return (
              <>
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Participant
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.25 }}>
                    {participantName}{participantLabelSuffix}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Trek
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.25 }}>
                    {trekName}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Batch dates
                  </Typography>
                  <Typography variant="body2">{batchDates}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Registration #{b.registrationId ?? b.registration?.id ?? '—'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: showPaymentRuleHint ? 0.5 : 2 }}
                >
                  Participant share — Total: {formatAdminInr(participantTotal)} · Advance: {formatAdminInr(participantAdvance)} · Paid:{' '}
                  {formatAdminInr(participantPaid)} · Pending: {formatAdminInr(participantPending)}
                </Typography>
                {advanceInfo.isWithinAdvanceWindow && total > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Departure within {ADVANCE_WINDOW_DAYS} days — full amount is due now.
                  </Typography>
                )}
                {!advanceInfo.isWithinAdvanceWindow &&
                  hasPartialAdvanceStructure(total, advance) && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {participantCount > 1
                        ? `Advance ₹5,000 per participant (₹${Math.round(participantAdvance * participantCount).toLocaleString('en-IN')} for ${participantCount}); remaining balance ${REMAINING_DUE_DAYS_BEFORE_DEPARTURE} days before departure.`
                        : `Advance ₹5,000 now; remaining balance ${REMAINING_DUE_DAYS_BEFORE_DEPARTURE} days before departure.`}
                    </Typography>
                  )}
                {(participantPending > 0 || showParticipantAdvanceFill) && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {participantPending > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setReceiptForm((p) => ({ ...p, amount: String(Math.round(participantPending)) }))
                        }
                      >
                        Fill participant pending ({formatAdminInr(participantPending)})
                      </Button>
                    )}
                    {showParticipantAdvanceFill && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setReceiptForm((p) => ({ ...p, amount: String(Math.round(participantAdvanceDue)) }))
                        }
                      >
                        Fill participant advance ({formatAdminInr(participantAdvanceDue)})
                      </Button>
                    )}
                  </Box>
                )}
              </>
            );
          })()}
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Payment method</InputLabel>
            <Select
              label="Payment method"
              value={receiptForm.method}
              onChange={(e) => setReceiptForm((p) => ({ ...p, method: e.target.value }))}
            >
              {RECEIPT_PAYMENT_METHOD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Amount (₹)"
            type="number"
            value={receiptForm.amount}
            onChange={(e) => setReceiptForm((p) => ({ ...p, amount: e.target.value }))}
            margin="normal"
            inputProps={{ min: 0, step: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
            Receipt date: <strong>{receiptModal.booking ? formatDate(new Date().toISOString().slice(0, 10)) : '—'}</strong>{' '}
            (today)
          </Typography>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={2}
            value={receiptForm.notes}
            onChange={(e) => setReceiptForm((p) => ({ ...p, notes: e.target.value }))}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setReceiptModal({
                open: false,
                booking: null,
                bookingLoading: false,
                participantName: null,
                participantIndex: null,
                participantCount: 1
              })
            }
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={submitReceipt} disabled={addReceiptMutation.isLoading || receiptModal.bookingLoading || !receiptForm.amount}>
            {addReceiptMutation.isLoading ? 'Saving...' : 'Save receipt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
